import { getStoreId } from '@utils/EnvironmentUtil'
import {
    InvalidInputException,
    UnsupportedBrowserException,
    VoiceNotEnabledException,
    LRUCache,
    LatencyMetric,
    CountMetric,
    VoiceDependencyFailureException,
    AudioException,
} from '@amzn/ihm-associate-tools-js-lib'
import * as AudioPlayer from '@utils/AudioPlayer'
import * as VoiceUtilsService from '@utils/VoiceUtilsService'
import _ from 'lodash'
import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import * as AwsClientProviderService from '@services/AwsClientProviderService'
import * as UserRoleService from '@services/UserRoleService'

// This is an arbitrary value and will be adjusted based on cache metrics
const SPEECH_SYNTHESIS_SERVICE_LRU_CACHE_CAPACITY = 20
const NON_FAULT_EXCEPTION_WHITELIST = Object.freeze([
    InvalidInputException,
    UnsupportedBrowserException,
    VoiceNotEnabledException,
])
let lastPlayedUrl

// @link https://docs.aws.amazon.com/AWSJavaScriptSDK/latest/AWS/Polly.html#synthesizeSpeech-property
const defaultPollyParams = {
    OutputFormat: 'mp3',
    SampleRate: '8000',
    TextType: 'text',
    VoiceId: 'Joanna',
    LanguageCode: 'en-US',
}

const audioPlayerDefaultParams = Object.freeze({
    priority: AudioPlayer.AudioPriority.VOICE,
})

const lruCache = new LRUCache(SPEECH_SYNTHESIS_SERVICE_LRU_CACHE_CAPACITY)
lruCache.setItem('metrics', {
    operation: 'speechSynthesisService.urlCache',
})
lruCache.setItem('removeCallback', removeCallback)
lruCache.setItem('updateCallback', removeCallback)

// Public functions
/**
 * To keep track of the last call of params and callback for unit testing in SpeechSynthesisService.test.ts.
 */
export const trackPollySynthesizeSpeech = (p, c) => { }

/**
 * To clear the last played url in memory.
 */
export const clearLastPlayedUrl = () => {
    lastPlayedUrl = null
}

/**
 * Warms up the service so that it's ready to make requests as soon as possible. Callers of this
 * function are encouraged to catch any errors and keep going. Warmup is just needed to reduce
 * latency, but calls to synthesizeSpeech should work if the error is transient.
 *
 * @return {Promise} Resolves if the warmup was successful and fails otherwise.
 */
export const warmup = () => {
    const warmupLatencyMetric = new LatencyMetric(getStoreId(), 'Time', 'IhmFlyingDutchman', 'speechSynthesisService.warmup')
    const warmupFaultMetric = new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'speechSynthesisService.warmup')
    return VoiceUtilsService.verifyVoiceIsEnabled()
        .then(() => {
            warmupLatencyMetric.start()
            return getClient()
        })
        .then(() => {
            // We swallow the client here because we just want the client generation to be cached. Also, we don't
            // want to expose the client to the caller (this is a public function).
            // A call to speak should get the same client we would get here.
            warmupLatencyMetric.stop()
            warmupFaultMetric.count(0)
        })
        .catch((err) => {
            return handleError(err, warmupFaultMetric)
        })
}

/**
 * Synthesizes speech based on the text provided.
 *
 * @param {object} config - Object containing the request for the speech synthesis request. Can also be a string if the caller
 *  doesn't need to pass a configuration and wants to pass the text directly.
 *  Example:
 *      {
 *          text: {string} - (Required) Text to synthesize.
 *      }
 * @return {Promise} A promise that resolves if the synthesis was done successfully and the audio played without
 *  any interruptions. This can reject on multiple scenarios:
 *      - If the required fields are not passed in the config object.
 *      - If there's a problem with the underlying client (Polly) creation or failure to assume the VOICE_USER_ROLE.
 *      - If there's a problem synthesizing the speech.
 *      - If there's a higher priority audio playing.
 *      - If a higher or equal priority audio starts playing.
 */
export const speak = (config) => {
    const speakFaultMetric = new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'speechSynthesisService.speak')

    if (!config) {
        new CountMetric(getStoreId(), 'InvalidInput', 'IhmFlyingDutchman', 'speechSynthesisService.synthesizeSpeech').count(1)
        speakFaultMetric.count(0)
        return Promise.reject(new InvalidInputException({
            message: 'the config object is required',
        }))
    }

    if (_.isString(config)) {
        config = {
            text: config,
        }
    }

    if (!config.text || !_.isString(config.text)) {
        new CountMetric(getStoreId(), 'InvalidInput', 'IhmFlyingDutchman', 'speechSynthesisService.synthesizeSpeech').count(1)
        speakFaultMetric.count(0)
        return Promise.reject(new InvalidInputException({
            message: 'the text key must be a non-empty string',
        }))
    }
    new CountMetric(getStoreId(), 'InvalidInput', 'IhmFlyingDutchman', 'speechSynthesisService.synthesizeSpeech').count(0)

    let audioStream

    // Track the time it takes to get the client and synthesize the speech so that it's ready for the
    // audio to be played.
    const latencyToAudioFeedback = new LatencyMetric(getStoreId(), 'LatencyToAudioFeedback', 'IhmFlyingDutchman', 'speechSynthesisService.synthesizeSpeech')
    return VoiceUtilsService.verifyVoiceIsEnabled()
        .then(setupMedia)
        .then((stream) => {
            audioStream = stream
        })
        .then(() => {
            latencyToAudioFeedback.start()
            return getClient()
        })
        .then((polly) => {
            return synthesizeSpeech(polly, config)
        })
        .then((url) => {
            // We stop here because we don't care how long playAudio takes since it's totally
            // dependent on how long the audio actually is.
            latencyToAudioFeedback.stop()
            return playAudio(url)
        })
        .then(() => {
            speakFaultMetric.count(0)
        })
        .catch((e) => {
            return handleError(e, speakFaultMetric)
        })
        .finally(() => {
            closeStream(audioStream)
        })
}

/**
 * Repeats the last synthesized audio (if any). Rejects if nothing was played last.
 * @return {Promise} - success when the audio is done playing.
 */
export const repeat = () => {
    if (!lastPlayedUrl) {
        new CountMetric(getStoreId(), 'InvalidInput', 'IhmFlyingDutchman', 'speechSynthesisService.repeat').count(1)

        return Promise.reject(new InvalidInputException({
            message: 'Nothing to repeat',
        }))
    }
    new CountMetric(getStoreId(), 'InvalidInput', 'IhmFlyingDutchman', 'speechSynthesisService.repeat').count(0)
    return playAudio(lastPlayedUrl)
}

// Private functions

/**
 * Gets the Polly client instance. We don't cache this promise because the underlying implementation could return a new
 * client if the credentials expire.
 */
const getClient = () => {
    if (!window.Blob) {
        new CountMetric(getStoreId(), 'Unsupported:Blob', 'IhmFlyingDutchman', 'speechSynthesisService').count(1)
        console.warn('SynthesizeSpeech will not work because this is an unsupported browser (expected window.Blob)')
        return Promise.reject(new UnsupportedBrowserException({
            message: 'Blob is not supported in this browser',
        }))
    }

    if (!window.URL) {
        new CountMetric(getStoreId(), 'Unsupported:URL', 'IhmFlyingDutchman', 'speechSynthesisService').count(1)
        console.warn('SynthesizeSpeech will not work because this is an unsupported browser (expected window.URL)')
        return Promise.reject(new UnsupportedBrowserException({
            message: 'URL is not supported in this browser',
        }))
    }
    return AwsClientProviderService.getAwsClient(AwsClientProviderService.AwsClient.POLLY, UserRoleService.UserRole.VOICE_USER_ROLE, '')
        .catch((err) => {
            console.warn('Error creating the client', err)
            return Promise.reject(new VoiceDependencyFailureException({
                message: 'There was a problem creating the client',
            }))
        })
}

/**
 * Calls Polly to synthesize the speech. Returns a Promise with the URL of the synthesized audio.
 */
const synthesizeSpeech = (polly, config) => {
    const cached = lruCache.getItem(config.text)
    if (cached) {
        return Promise.resolve(cached)
    }
    const deferred = generateDeferredPromise<any>()

    const pollyLatencyMetric = new LatencyMetric(getStoreId(), 'Time', 'IhmFlyingDutchman', 'polly.synthesizeSpeech')
    pollyLatencyMetric.start()
    const params = getPollyParamsForConfig(config)
    const callCallback = (err, data) => {
        pollyLatencyMetric.stop()

        if (err) {
            console.error('Error calling Polly', err.code, err.message, err)
            new CountMetric(getStoreId(), 'Error:' + err.code, 'IhmFlyingDutchman', 'polly.synthesizeSpeech').count(1)
            new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'polly.synthesizeSpeech').count(1)

            deferred.reject(new VoiceDependencyFailureException({
                message: 'Error synthesizing the speech',
            }))
            return
        }

        new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'polly.synthesizeSpeech').count(0)
        new CountMetric(getStoreId(), 'audioSize', 'IhmFlyingDutchman', 'polly.synthesizeSpeech').count(data.AudioStream.length)

        try {
            const uInt8Array = data.AudioStream
            const arrayBuffer = uInt8Array.buffer
            const blob = new window.Blob([arrayBuffer])
            const url = window.URL.createObjectURL(blob)
            lruCache.setItem(config.text, url)
            deferred.resolve(url)
        } catch (e) {
            console.error('Failed decoding the audio from Polly', e)
            deferred.reject(new VoiceDependencyFailureException({
                message: 'Failed decoding the audio from Polly',
            }))
        }
    }

    // To track params and callCallback for unit testing
    trackPollySynthesizeSpeech(params, callCallback)

    polly.synthesizeSpeech(params, callCallback)
    return deferred.promise
}

/**
 * Plays the audio form the URL passed as the argument.
 */
const playAudio = (url) => {
    lastPlayedUrl = url
    return AudioPlayer.playSoundBySource(url, audioPlayerDefaultParams)
}

/**
 * Creates a params Object that can be passed to Polly.
 */
const getPollyParamsForConfig = (config) => {
    const params = Object.assign({}, defaultPollyParams)
    const paramsWithTxt = Object.assign(params, { Text: '' })
    paramsWithTxt.Text = config.text
    return paramsWithTxt
}

/**
 * Revokes the objectUrl once the URL is no longer relevant
 * We pass this function to the cache, so it can be executed when needed
 */
function removeCallback (key, value) {
    window.URL.revokeObjectURL(value)
    console.debug(value, 'revoked for text:', key)
    new CountMetric(getStoreId(), 'RevokeURL', 'IhmFlyingDutchman', 'speechSynthesisService.removeCallback').count(1)
}

const handleError = (err, faultMetric) => {
    const inFaultWhitelist = NON_FAULT_EXCEPTION_WHITELIST.some((ExceptionConstructor) => {
        return err instanceof ExceptionConstructor
    })

    if (inFaultWhitelist) {
        faultMetric.count(0)
        return Promise.reject(err)
    }

    faultMetric.count(1)
    return Promise.reject(err)
}

/**
 *  Sets up the media and returns an audio stream.
 *
 *  @return {Promise} Promise that resolves when the MediaStream {@link https://developer.mozilla.org/en-US/docs/Web/API/MediaStream}
 *      is available. May reject if
 *          - The audio stream is not available (for example, the user decided not to consent to enable microphone).
 *          - We're requesting the audio stream too early and the user hasn't interacted with the tools yet.
 *          - The page was not rendered using HTTPS.
 */
const setupMedia = () => {

    const getUserMediaLatencyMetric = new LatencyMetric(getStoreId(), 'Time', 'IhmFlyingDutchman', 'speechSynthesisService.getUserMedia')
    getUserMediaLatencyMetric.start()

    // This could reject if the user hasn't interacted enough with the page.
    // Wrapped in a $q promise since getUserMedia returns a native promise
    return Promise.resolve(window.navigator.mediaDevices.getUserMedia({
        audio: true,
    }))
        .then((stream) => {
            getUserMediaLatencyMetric.stop()
            console.debug('Setting up stream', stream.id)
            return stream
        })
        .catch((e) => {
            console.warn('Error with getUserMedia', e)
            return Promise.reject(new AudioException({
                message: 'Could not initialize user media',
            }))
        })
}

/**
 * Stops all the tracks from the Stream so that the microphone can be used in other places.
 */
const closeStream = (audioStream) => {
    if (!audioStream) {
        console.debug('No streams found')
        return
    }
    try {
        console.debug('Closing stream', audioStream.id)
        audioStream.getAudioTracks()
            .forEach((track) => {
                track.stop()
            })
    } catch (e) {
        new CountMetric(getStoreId(), 'Fault', 'IhmFlyingDutchman', 'speechSynthesisService.closeStream').count(1)
        console.error('Error closing the streams', e)
    }
}
